import {
  ArmyList,
  ArmyListFormated,
  ArmyListUnit,
  ArmyListUnitModelRuleTroop,
  ArmyListUnitTroop,
  ArmyListUnitOption,
  ArsenalArmy,
  ArsenalUnit,
  ArsenalUnitStep,
  Nullable,
  PaintStep,
  SyncEntityState,
  VersionGame,
} from '@Models';
import { TYPES } from '@Types';
import { RequestAction } from '@Repository/RequestAction';
import {
  ArmyListRepository,
  FormatRepository,
  RequestArmyListSuccess,
} from '@Repository/builder';
import { Locale } from '@Repository/locale';
import { inject, injectable } from 'inversify';
import { Result } from '@Redux/type';
import { RequestArmyListFormatedSuccess } from '@Repository/builder/response';
import { change } from 'redux-form';
import { DeleteRepository } from '@Repository/DeleteRepository';
import { v4 as uuidv4 } from 'uuid';
import { ArmyListFormatedMapper } from '@Mapper/builder/import/army-list-formated.mapper';
import { ArmyAction, MyArsenalAction } from '@Redux/actions';
import { PayloadAction } from '@reduxjs/toolkit';
import { ArmyListCreateParams } from '@Builder/myBuilder/builder/new/army_list/validate';
import { ArmyListEditParams } from '@Builder/myArmyList/edit/validate';
import { UserFavoriteUnitsAction } from '@Redux/actions/ninthAge/user-favorite-units.action';
import { IEditableEntityAction } from '@Redux/IEditableEntityAction';
import armyListSlice from '@Redux/reducers/builder/armyList.slice';
import {
  getArmyList,
  getOrganisations,
  getUnits,
} from '@Selectors/builder/myArmyList/make/unit/edit/selector/editSelector';

@injectable()
export class ArmyListAction extends IEditableEntityAction<
  string,
  ArmyList,
  RequestArmyListSuccess
> {
  @inject(TYPES.Repository.ArmyListRepository)
  private _repository!: ArmyListRepository;
  @inject(TYPES.Repository.FormatRepository)
  private _formatRepository!: FormatRepository;
  @inject(TYPES.Mapper.ArmyListFormatedMapper)
  private _armyListFormatedMapper!: ArmyListFormatedMapper;
  @inject(TYPES.Actions.ArmyAction) private _armyAction!: ArmyAction;
  @inject(TYPES.Locale) private _locale!: Locale;
  @inject(TYPES.Actions.MyArsenalAction)
  private _arsenalAction!: MyArsenalAction;
  @inject(TYPES.Actions.NinthAge.FavoriteUnit.UserFavoriteUnitsAction)
  private _userFavoriteUnitsAction!: UserFavoriteUnitsAction;

  getDeleteRepository(): DeleteRepository<string> {
    return this._repository;
  }

  public formatArmyList =
    (date: number, game: VersionGame, source: string): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const response = await this._formatRepository.format({
        date: date,
        game: game,
        source: source,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestArmyListFormatedSuccess;
      dispatch(
        change('format_army_list', 'formated', success.payload.formated),
      );
      dispatch(change('format_army_list', 'errors', success.payload.errors));
      dispatch(change('format_army_list', 'formatedArmyList', success.payload));
      dispatch(this.areLoading(false));
    };

  public importArmyList =
    (armyListFormated: ArmyListFormated): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const armyList =
        this._armyListFormatedMapper.mapToBuilder(armyListFormated);

      const response = await this._repository.create({
        armyList: armyList,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestArmyListSuccess;
      window.location.href = `/${this._locale.getlocale()}/builder/army_lists/${
        success.payload.id
      }`;
    };

  public duplicate =
    (armyList: ArmyList): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const newArmyList = {
        ...armyList,
        id: '',
        name: `${armyList.name} copy`,
        army_list_units: armyList.army_list_units.map(
          (unit) =>
            ({
              ...unit,
              id: '',
              army_list_unit_unit_options: unit.army_list_unit_unit_options.map(
                (option) =>
                  ({
                    ...option,
                    id: '',
                  }) satisfies ArmyListUnitOption as ArmyListUnitOption,
              ),
              modelRuleUnitTroops: unit.modelRuleUnitTroops.map(
                (rule) =>
                  ({
                    ...rule,
                    id: '',
                  }) satisfies ArmyListUnitModelRuleTroop as ArmyListUnitModelRuleTroop,
              ),
              troops: unit.troops.map(
                (troop) =>
                  ({
                    ...troop,
                    id: '',
                  }) satisfies ArmyListUnitTroop as ArmyListUnitTroop,
              ),
            }) satisfies ArmyListUnit as ArmyListUnit,
        ),
      } satisfies ArmyList as ArmyList;

      const response = await this._repository.create({
        armyList: newArmyList,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      dispatch(this.areLoading(false));

      const success = response as RequestArmyListSuccess;
      window.location.href = `/${this._locale.getlocale()}/builder/army_lists/${
        success.payload.id
      }`;
    };

  public create =
    (values: ArmyListCreateParams): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const armyList = {
        id: uuidv4(),
        folderId: values.folderId != '' ? values.folderId : null,
        versionId: values.versionId,
        armyId: values.armyId,
        name: values.name,
        max: values.max,
        isPrivate: values.isPrivate,
        notes: values.notes,
        army_list_units: [],
        initials: '',
        army_organisation_id: 0,
        points: 0,
        versionGame: VersionGame.NinthAge,
        size: 0,
        versionName: '',
        updatedAt: 0,
        versionColor: '',
        creator: '',
        largeLogo: '',
      } satisfies ArmyList as ArmyList;

      const response = await this._repository.create({
        armyList: armyList,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestArmyListSuccess;
      window.location.href = `/${this._locale.getlocale()}/builder/army_lists/${
        success.payload.id
      }`;
    };

  public edit =
    (armyList: ArmyList): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const response = await this._repository.update({
        id: armyList.id,
        armyList: armyList,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestArmyListSuccess;
      dispatch(armyListSlice.actions.setArmyList(success.payload));
      dispatch(this.areLoading(false));
    };

  public createPaintList =
    (armyList: ArmyList): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const state = getState();
      const army = state.army.entity;
      if (!army) {
        dispatch(this.areLoading(false));
        return;
      }

      const arsenalAry = {
        id: '',
        name: armyList.name,

        armyCategoryId: army.categoryId,
        units: armyList.army_list_units.map(
          (unit) =>
            ({
              id: '',
              name: unit.name,
              unitCategoryId:
                army.units.find((u) => u.id === unit.unit_id)?.unitCategoryId ??
                0,
              mountCategoryId: null,
              unitIds: [unit.unit_id],

              position: unit.position,
              comment: null,

              steps: [
                {
                  id: '',
                  size: unit.size,
                  step: PaintStep.Project,
                  state: SyncEntityState.ToCreate,
                } satisfies ArsenalUnitStep as ArsenalUnitStep,
              ],
            }) satisfies ArsenalUnit as ArsenalUnit,
        ),
        steps: [],
        logo: armyList.largeLogo,
        largeLogo: armyList.largeLogo,
        updatedAt: 0,
      } satisfies ArsenalArmy as ArsenalArmy;

      dispatch(this._arsenalAction.create(arsenalAry));
    };

  public save =
    (values: ArmyListEditParams): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const armyList = getArmyList(getState());
      if (!armyList) return;

      const editArmyList = {
        ...armyList,
        folderId: values.folderId != '' ? values.folderId : null,
        name: values.name,
        max: values.max,
        isPrivate: values.isPrivate,
        notes: values.notes,
      } satisfies ArmyList as ArmyList;

      dispatch(this.fetchDataSuccess(editArmyList));
      dispatch(this.edit(editArmyList));
    };

  public sortArmyListAction =
    (): Result<void> => async (dispatch, getState) => {
      dispatch(this.areLoading(true));
      const state = getState();

      const units = getUnits(state);
      const organisations = getOrganisations(state);

      const armyList = getArmyList(state);
      if (!armyList) return;

      const armyListUnits = organisations
        .flatMap((organisation) => {
          return armyList.army_list_units
            .filter((unit) => unit.organisation_id === organisation.id)
            .sort((a, b) => {
              if (a.unit_id === b.unit_id) return a.points - b.points;

              const unitA = units.find((item) => item.id === a.unit_id);
              const unitB = units.find((item) => item.id === b.unit_id);

              return (unitA?.position ?? 0) - (unitB?.position ?? 0);
            });
        })
        .map(
          (armyListUnit, index) =>
            ({
              ...armyListUnit,
              position: index,
            }) satisfies ArmyListUnit as ArmyListUnit,
        );

      const editArmyList = {
        ...armyList,
        army_list_units: armyListUnits,
      } satisfies ArmyList as ArmyList;

      dispatch(this.fetchDataSuccess(editArmyList));
      dispatch(this.edit(editArmyList));
    };

  protected fetchDataSuccess(item: ArmyList): PayloadAction<ArmyList> {
    return armyListSlice.actions.setArmyList(item);
  }

  protected cacheDataSuccess(item: ArmyList): PayloadAction<ArmyList> {
    return armyListSlice.actions.setArmyList(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return armyListSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return armyListSlice.actions.setLoading(bool);
  }

  protected getCachedItem({
    entityId,
  }: {
    entityId: string;
  }): Promise<Nullable<ArmyList>> {
    return this._repository.getCachedEntity({ dataId: entityId, locale: null });
  }

  protected async getItem({
    entityId,
  }: {
    entityId: string;
  }): Promise<RequestAction> {
    return this._repository.getArmyList({ id: entityId, locale: null });
  }

  protected subFetchDataBefore =
    ({ entity }: { entity: ArmyList }): Result<void> =>
    (dispatch) => {
      dispatch(this._armyAction.fetchData({ entityId: entity.armyId }));
      dispatch(this._userFavoriteUnitsAction.fetchData(entity.armyId));
    };

  protected subFetchDataAfter =
    ({
      entity,
      cachedItem,
    }: {
      entity: ArmyList;
      cachedItem: Nullable<ArmyList>;
    }): Result<void> =>
    (dispatch) => {
      if (!cachedItem || cachedItem?.armyId != entity.armyId) {
        dispatch(this._armyAction.fetchData({ entityId: entity.armyId }));
      }
    };
}
